/**
 * Identity linking functions for authentication orchestrator
 * Handles email/phone linking, OTP verification, and account merging
 */

import type { AuthPhase } from '@/types/profileTypes';
import { formatPhoneToE164 } from '@/utils/phone-formatter';
import {
  IdentityType,
  type AuthLinkEmailMutation,
  type AuthLinkPhoneMutation,
  type AuthMigrateIdentityMutation,
  type MigrateIdentityInput,
  type AuthUserOutput,
  type UserPortfolioQuery
} from '@/generated/nexus-graphql';
import type { ComputedRef } from 'vue';
import type { Store } from 'vuex';
import type { NavigationService } from './useAuthNavigationService';
import type { AuthOrchestratorState } from './useAuthState';
import type { RootState } from '../types';
import { ERROR_MESSAGES } from '../constants';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('AuthIdentity');

interface LinkEmailInput {
  input: {
    email: string;
    otpCode?: string;
    forceMerge?: boolean;
  };
}

interface LinkPhoneInput {
  input: {
    phoneNumber: string;
    otpCode?: string;
    forceMerge?: boolean;
  };
}

export function useAuthIdentity(
  state: AuthOrchestratorState,
  deps: {
    linkEmailMutate: (params: LinkEmailInput) => Promise<{
      data?: AuthLinkEmailMutation;
      errors?: Array<{ message: string }>;
    }>;
    linkPhoneMutate: (params: LinkPhoneInput) => Promise<{
      data?: AuthLinkPhoneMutation;
      errors?: Array<{ message: string }>;
    }>;
    migrateIdentityMutate: (params: { input: MigrateIdentityInput }) => Promise<{
      data?: AuthMigrateIdentityMutation;
      errors?: Array<{ message: string }>;
    }>;
    getUser?: ComputedRef<AuthUserOutput | null>;
    portfolioData?: ComputedRef<UserPortfolioQuery | null>;
    rootStore?: Store<RootState>;
    nextPhase: () => void;
    navigationService?: NavigationService;
  }
) {
  /**
   * Check if user has an alternate contact method
   */
  const hasAlternateContact = (): boolean => {
    const user = deps.getUser?.value;
    const portfolioUser = deps.portfolioData?.value?.userPortfolio?.user;
    const currentUser = portfolioUser || user;
    if (!currentUser) return false;

    if (state.isEmail) {
      // If user signed up with email, check for phone
      return !!currentUser.phoneNumber;
    }
    // If user signed up with phone, check for email
    return !!currentUser.email;
  };

  /**
   * Check if alternate contact phase is accessible
   */
  const isAlternateContactPhaseAccessible = (): boolean => {
    // Can't go back to alternate contact if user already added one
    if (hasAlternateContact()) {
      logger.log('User already has alternate contact, cannot go back');
      return false;
    }

    // Can't go back if user explicitly skipped
    if (state.alternateContactSkipped) {
      logger.log('User skipped alternate contact, cannot go back');
      return false;
    }

    // Can't go back if we've passed certain gates
    if (state.passedGates?.has('accountDiscovery') || state.passedGates?.has('identityConflict')) {
      logger.log('Passed critical gates, cannot go back to alternate contact');
      return false;
    }

    return true;
  };

  /**
   * Request email linking
   */
  const requestEmailLinking = async (email: string) => {
    logger.log('Requesting email linking');
    state.loading = true;
    state.error = null;

    state.pendingIdentityLink = {
      type: 'email',
      value: email,
      otpSent: false
    };

    try {
      // First attempt to link without OTP - backend will request OTP if needed
      const result = await deps.linkEmailMutate({
        input: {
          email
        }
      });

      // Check for GraphQL errors first
      if (result?.errors) {
        const errorMessage = result.errors[0]?.message || 'GraphQL error during email linking';
        logger.error('GraphQL error in email linking:', errorMessage);
        throw new Error(errorMessage);
      }

      const linkResult = result?.data?.authLinkEmail;

      if (!linkResult) {
        throw new Error(ERROR_MESSAGES.GENERIC);
      }

      if (linkResult.requiresVerification) {
        // Backend initiated OTP flow
        state.pendingIdentityLink.otpSent = true;
        if (deps.navigationService) {
          await deps.navigationService.navigate('identityLinkingOtp');
        } else {
          state.phase = 'identityLinkingOtp';
        }
      } else if (linkResult.conflict) {
        // Handle conflict
        state.pendingIdentityLink.conflictingUserId = linkResult.conflictUserId;
        state.pendingIdentityLink.canMerge = linkResult.canMerge;
        if (deps.navigationService) {
          await deps.navigationService.navigate('identityConflict');
        } else {
          state.phase = 'identityConflict';
        }
        state.error = linkResult.message || ERROR_MESSAGES.IDENTITY_CONFLICT;
      } else if (linkResult.success) {
        // Successfully linked without OTP (shouldn't happen normally)
        state.pendingIdentityLink = { type: null, value: '', otpSent: false };
        deps.nextPhase();
      } else {
        throw new Error(linkResult.message || ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      logger.error('Email linking request failed');
      state.error = ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Request phone linking
   */
  const requestPhoneLinking = async (phoneNumber: string) => {
    logger.log('Requesting phone linking');
    state.loading = true;
    state.error = null;

    // Format phone for E.164
    const formattedPhone = formatPhoneToE164(phoneNumber);

    state.pendingIdentityLink = {
      type: 'phone',
      value: formattedPhone,
      otpSent: false
    };

    try {
      // First attempt to link without OTP - backend will request OTP if needed
      const result = await deps.linkPhoneMutate({
        input: {
          phoneNumber: formattedPhone
        }
      });

      // Check for GraphQL errors first
      if (result?.errors) {
        const errorMessage = result.errors[0]?.message || 'GraphQL error during phone linking';
        logger.error('GraphQL error in phone linking:', errorMessage);
        throw new Error(errorMessage);
      }

      const linkResult = result?.data?.authLinkPhone;

      if (!linkResult) {
        throw new Error(ERROR_MESSAGES.GENERIC);
      }

      if (linkResult.requiresVerification) {
        // Backend initiated OTP flow
        state.pendingIdentityLink.otpSent = true;
        if (deps.navigationService) {
          await deps.navigationService.navigate('identityLinkingOtp');
        } else {
          state.phase = 'identityLinkingOtp';
        }
      } else if (linkResult.conflict) {
        // Handle conflict
        state.pendingIdentityLink.conflictingUserId = linkResult.conflictUserId;
        state.pendingIdentityLink.canMerge = linkResult.canMerge;
        if (deps.navigationService) {
          await deps.navigationService.navigate('identityConflict');
        } else {
          state.phase = 'identityConflict';
        }
        state.error = linkResult.message || ERROR_MESSAGES.IDENTITY_CONFLICT;
      } else if (linkResult.success) {
        // Successfully linked without OTP (shouldn't happen normally)
        state.pendingIdentityLink = { type: null, value: '', otpSent: false };
        deps.nextPhase();
      } else {
        throw new Error(linkResult.message || ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      logger.error('Phone linking request failed');
      state.error = ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Verify OTP for identity linking
   */
  const verifyIdentityLinkingOtp = async (otpCode: string) => {
    logger.log('Verifying identity linking OTP');
    state.loading = true;
    state.error = null;

    try {
      let result;

      if (state.pendingIdentityLink.type === 'email') {
        result = await deps.linkEmailMutate({
          input: {
            email: state.pendingIdentityLink.value,
            otpCode,
            forceMerge: state.pendingIdentityLink.mergeIntended || false
          }
        });

        // Check for GraphQL errors first
        if (result?.errors) {
          const errorMessage =
            result.errors[0]?.message || 'GraphQL error during email OTP verification';
          logger.error('GraphQL error in email OTP verification:', errorMessage);
          throw new Error(errorMessage);
        }

        const linkResult = result?.data?.authLinkEmail;

        if (linkResult?.conflict) {
          // Handle conflict
          state.pendingIdentityLink.conflictingUserId = linkResult.conflictUserId;
          state.pendingIdentityLink.canMerge = linkResult.canMerge;
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityConflict');
          } else {
            state.phase = 'identityConflict';
          }
          state.error = linkResult.message || ERROR_MESSAGES.IDENTITY_CONFLICT;
        } else if (linkResult?.success) {
          // Successfully linked
          state.pendingIdentityLink = { type: null, value: '', otpSent: false };
          deps.nextPhase(); // Continue to next profile phase
        } else {
          throw new Error(linkResult?.message || 'Failed to link email');
        }
      } else if (state.pendingIdentityLink.type === 'phone') {
        result = await deps.linkPhoneMutate({
          input: {
            phoneNumber: state.pendingIdentityLink.value,
            otpCode,
            forceMerge: state.pendingIdentityLink.mergeIntended || false
          }
        });

        // Check for GraphQL errors first
        if (result?.errors) {
          const errorMessage =
            result.errors[0]?.message || 'GraphQL error during phone OTP verification';
          logger.error('GraphQL error in phone OTP verification:', errorMessage);
          throw new Error(errorMessage);
        }

        const linkResult = result?.data?.authLinkPhone;

        if (linkResult?.conflict) {
          // Handle conflict
          state.pendingIdentityLink.conflictingUserId = linkResult.conflictUserId;
          state.pendingIdentityLink.canMerge = linkResult.canMerge;
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityConflict');
          } else {
            state.phase = 'identityConflict';
          }
          state.error = linkResult.message || ERROR_MESSAGES.IDENTITY_CONFLICT;
        } else if (linkResult?.success) {
          // Successfully linked
          state.pendingIdentityLink = { type: null, value: '', otpSent: false };
          deps.nextPhase(); // Continue to next profile phase
        } else {
          throw new Error(linkResult?.message || 'Failed to link phone number');
        }
      }
    } catch (error) {
      logger.error('Identity linking failed');
      state.error =
        error instanceof Error ? error.message : 'Failed to verify code. Please try again.';
    } finally {
      state.loading = false;
    }
  };

  /**
   * Skip identity linking
   */
  const skipIdentityLinking = () => {
    // Clear pending link and continue
    state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    deps.nextPhase();
  };

  /**
   * Merge accounts
   */
  const mergeAccounts = async (otpCode?: string) => {
    logger.log('Attempting to merge accounts');
    state.loading = true;
    state.error = null;

    if (!state.pendingIdentityLink.conflictingUserId) {
      state.error = ERROR_MESSAGES.GENERIC;
      state.loading = false;
      return;
    }

    try {
      // If we don't have an OTP code yet, request one
      if (!otpCode) {
        // Request OTP for the pending identity
        if (state.pendingIdentityLink.type === 'email') {
          // Request email OTP
          // The backend will send OTP when we try to migrate
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityLinkingOtp');
          } else {
            state.phase = 'identityLinkingOtp';
          }
          state.pendingIdentityLink.otpSent = true;
        } else if (state.pendingIdentityLink.type === 'phone') {
          // Request phone OTP
          if (deps.navigationService) {
            await deps.navigationService.navigate('identityLinkingOtp');
          } else {
            state.phase = 'identityLinkingOtp';
          }
          state.pendingIdentityLink.otpSent = true;
        }
        state.loading = false;
        return;
      }

      // We have an OTP code, proceed with migration
      const identityType =
        state.pendingIdentityLink.type === 'email' ? IdentityType.Email : IdentityType.Phone;

      const result = await deps.migrateIdentityMutate({
        input: {
          fromUserId: state.pendingIdentityLink.conflictingUserId,
          identifier: state.pendingIdentityLink.value,
          identityType,
          verificationCode: otpCode
        }
      });

      // Check for GraphQL errors first
      if (result?.errors) {
        const errorMessage = result.errors[0]?.message || 'GraphQL error during account migration';
        logger.error('GraphQL error in account migration:', errorMessage);
        throw new Error(errorMessage);
      }

      const migrateResult = result?.data?.authMigrateIdentity;

      if (migrateResult?.success) {
        // Successfully migrated
        logger.log('Account migration successful');
        state.pendingIdentityLink = { type: null, value: '', otpSent: false };

        // Continue to next phase or complete
        deps.nextPhase();
      } else {
        throw new Error(migrateResult?.message || 'Failed to merge accounts');
      }
    } catch (error) {
      logger.error('Account merge failed');
      state.error = ERROR_MESSAGES.MERGE_FAILED;
      // Go back to conflict phase
      if (deps.navigationService) {
        await deps.navigationService.navigate('identityConflict');
      } else {
        state.phase = 'identityConflict';
      }
    } finally {
      state.loading = false;
    }
  };

  /**
   * Proceed with merge when user confirms
   */
  const proceedWithMerge = async () => {
    logger.log('User confirmed account merge');
    if (state.pendingIdentityLink.canMerge) {
      // User explicitly chose to merge - set the flag
      state.pendingIdentityLink.mergeIntended = true;

      // Track that we're passing through the identity conflict gate
      state.passedGates.add('identityConflict');

      // Continue with the linking which will merge the accounts
      if (deps.navigationService) {
        await deps.navigationService.navigate('identityLinkingOtp');
      } else {
        state.phase = 'identityLinkingOtp'; // Fallback
      }
      // The actual merge will happen when they verify OTP with forceMerge flag
    } else {
      logger.error('Cannot merge - accounts belong to different users');
      state.error = ERROR_MESSAGES.MERGE_FAILED;
    }
  };

  /**
   * Switch to other account
   */
  const switchToOtherAccount = async () => {
    // Log out current user and redirect to login
    logger.log('Switching to other account');

    // Clear auth state and stored tokens
    if (deps.rootStore) {
      // Clear user session from store
      deps.rootStore.commit('user/LOGOUT_USER');
      // Clear any stored tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      sessionStorage.clear();
    }

    // Reset auth state to allow new login
    if (deps.navigationService) {
      // Use system transition for going back to contact phase
      await deps.navigationService.systemTransition('idle');
      await deps.navigationService.navigate('contact');
    } else {
      state.phase = 'contact'; // Fallback
    }
    state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    state.contact = '';
    state.isNewUser = false;

    // Keep the auth dialog open for re-authentication
    // User can now sign in with the other account
  };

  return {
    hasAlternateContact,
    isAlternateContactPhaseAccessible,
    requestEmailLinking,
    requestPhoneLinking,
    verifyIdentityLinkingOtp,
    skipIdentityLinking,
    mergeAccounts,
    proceedWithMerge,
    switchToOtherAccount
  };
}
